import { IStairCalculationResult, IStairConfig } from '@stair-craft/common/util';

import { horizontalDim, verticalDim } from './dim-builders';
import {
    EColorRef,
    EPrimitiveKind,
    ETextAlign,
    ETextBaseline,
    FONT_MONO_FAMILY,
    IFont,
    IPaneRect,
    IPlanPrimitive,
    IPoint,
    IProjectionOutput,
    ITextPrimitive,
    emptyProjection,
} from './plan-primitive';
import { ITextMeasurer } from './text-measurer';

const SUB_LABEL_FONT: IFont = { weight: 500, size: 10, family: FONT_MONO_FAMILY };

/** Per-flight override for stacked L/U drawings. */
interface IFlight {
    stepCount: number;
    totalRun: number;
    totalRise: number;
    stairAngle: number;
    subLabel?: string;
    /** Upper bound of the fit, so two stacked flights share one millimetre scale. */
    maxPxPerMm?: number;
}

function subLabelText(at: IPoint, value: string): ITextPrimitive {
    return {
        kind: EPrimitiveKind.Text,
        text: value,
        align: ETextAlign.Left,
        baseline: ETextBaseline.Top,
        color: EColorRef.Ink2,
        font: SUB_LABEL_FONT,
        at,
    };
}

/**
 * Тетива (string-board) — a flat board with diagonal mortise slots where the
 * treads/risers seat, plus length/width dimensions. 1:1 port of
 * PlanViewComponent#drawTetivaDrawing. Pane-local pixels.
 */
function projectTetiva(result: IStairCalculationResult, pane: IPaneRect, measurer: ITextMeasurer, flight?: IFlight): IProjectionOutput {
    const N: number = flight?.stepCount ?? result.stepCount;
    const totalRun: number = flight?.totalRun ?? result.totalRun;
    const totalRise: number = flight?.totalRise ?? result.room.floorHeight;
    const stairAngleDeg: number = flight?.stairAngle ?? result.stairAngle;
    const hypotenuse: number = Math.sqrt(totalRun * totalRun + totalRise * totalRise);
    const boardWidth: number = 218;
    const endExtra: number = 262;

    if (N <= 0 || hypotenuse <= 0) {
        return emptyProjection();
    }

    const totalLen: number = hypotenuse + endExtra * 2;

    const padL: number = 56;
    const padR: number = 40;
    const padT: number = 36;
    const padB: number = 36;
    const innerW: number = pane.w - padL - padR;
    const innerH: number = pane.h - padT - padB;
    const scale: number = Math.min(innerW / totalLen, (innerH * 0.65) / boardWidth, flight?.maxPxPerMm ?? Infinity);
    const drawW: number = totalLen * scale;
    const drawH: number = boardWidth * scale;
    // Pane-local: the drawer applies the pane origin. `pane.y` carries the
    // sub-pane offset the dispatcher assigns when stacking two flights.
    const ox: number = padL;
    const oy: number = pane.y + padT + (innerH - drawH) / 2;

    const primitives: IPlanPrimitive[] = [];

    // Board outline — flat horizontal rectangle.
    primitives.push({
        kind: EPrimitiveKind.Poly,
        points: [
            [ox, oy],
            [ox + drawW, oy],
            [ox + drawW, oy + drawH],
            [ox, oy + drawH],
        ],
        closed: true,
        fill: EColorRef.Paper2,
        stroke: EColorRef.Ink2,
        width: 1.4,
    });

    // Diagonal mortise slots (parallelograms angled at the stair slope).
    const stepHyp: number = hypotenuse / N;
    const stairAngleRad: number = (stairAngleDeg * Math.PI) / 180;
    const slotLen: number = boardWidth * 0.72;
    const slotDX: number = slotLen * Math.sin(stairAngleRad) * scale;
    const slotDY: number = slotLen * Math.cos(stairAngleRad) * scale;
    const slotW: number = stepHyp * 0.32 * scale;
    const slotInset: number = drawH * 0.08;

    for (let i: number = 0; i < N; i++) {
        const cx: number = ox + endExtra * scale + (i + 0.5) * stepHyp * scale;
        const y0: number = oy + drawH - slotInset;
        primitives.push({
            kind: EPrimitiveKind.Poly,
            points: [
                [cx - slotW / 2, y0],
                [cx - slotW / 2 + slotDX, y0 - slotDY],
                [cx + slotW / 2 + slotDX, y0 - slotDY],
                [cx + slotW / 2, y0],
            ],
            closed: true,
            fill: EColorRef.Step,
            stroke: EColorRef.StepStroke,
            width: 0.9,
        });
    }

    // Dimensions (zhitov m1 layout).
    primitives.push(horizontalDim(ox, oy - 28, ox + drawW, String(Math.round(totalLen)), measurer));
    primitives.push(horizontalDim(ox + endExtra * scale, oy - 14, ox + drawW - endExtra * scale, String(Math.round(hypotenuse)), measurer));
    primitives.push(horizontalDim(ox, oy + drawH + 18, ox + endExtra * scale, String(endExtra), measurer));
    primitives.push(
        horizontalDim(
            ox + endExtra * scale,
            oy + drawH + 18,
            ox + endExtra * scale + stepHyp * scale,
            String(Math.round(stepHyp)),
            measurer
        )
    );
    primitives.push(verticalDim(ox + drawW + 16, oy, oy + drawH, String(boardWidth), measurer));

    if (flight?.subLabel) {
        primitives.push(subLabelText([ox, oy - 36], flight.subLabel));
    }

    // Millimetre origin: the board's left end at its top edge; the board width grows down the screen.
    return { primitives, pxPerMm: scale, origin: { x: ox, y: oy } };
}

/**
 * Косоур (saw-tooth stringer) — a board with anchor cuts at both ends and a
 * sawtooth top edge the treads sit on, plus its dimensions. 1:1 port of
 * PlanViewComponent#drawKosourDrawing. Pane-local pixels.
 */
function projectKosour(result: IStairCalculationResult, pane: IPaneRect, measurer: ITextMeasurer, flight?: IFlight): IProjectionOutput {
    const N: number = flight?.stepCount ?? result.stepCount;
    const totalRun: number = flight?.totalRun ?? result.totalRun;
    const totalRise: number = flight?.totalRise ?? result.room.floorHeight;
    const stairAngleDeg: number = flight?.stairAngle ?? result.stairAngle;
    const hypotenuse: number = Math.sqrt(totalRun * totalRun + totalRise * totalRise);
    const angleRad: number = (stairAngleDeg * Math.PI) / 180;
    const tanA: number = Math.tan(angleRad);

    if (N <= 0 || hypotenuse <= 0) {
        return emptyProjection();
    }

    const bodyHeight: number = 295;
    const toothHeight: number = 148;
    const leftAnchorW: number = 123;
    const leftToothGap: number = 177;
    const rightToothGap: number = 91;
    const rightAnchorW: number = 109;
    const leftCutH: number = Math.max(0, bodyHeight - leftAnchorW * tanA);
    const rightCutH: number = Math.max(0, bodyHeight - rightAnchorW * tanA);

    const stepHyp: number = hypotenuse / N;
    const totalLen: number = leftAnchorW + leftToothGap + N * stepHyp + rightToothGap + rightAnchorW;

    const padL: number = 56;
    const padR: number = 40;
    const padT: number = 36;
    const padB: number = 40;
    const innerW: number = pane.w - padL - padR;
    const innerH: number = pane.h - padT - padB;
    const worldW: number = totalLen;
    const worldH: number = bodyHeight + toothHeight;
    const scale: number = Math.min(innerW / worldW, (innerH * 0.85) / worldH, flight?.maxPxPerMm ?? Infinity);
    const drawH: number = worldH * scale;
    // Pane-local: the drawer applies the pane origin. `pane.y` carries the
    // sub-pane offset the dispatcher assigns when stacking two flights.
    const ox: number = padL;
    const oy: number = pane.y + padT + (innerH - drawH) / 2;

    const wx: (mm: number) => number = (mm: number): number => ox + mm * scale;
    const wy: (mm: number) => number = (mm: number): number => oy + (worldH - mm) * scale;

    const primitives: IPlanPrimitive[] = [];

    // Outline (CCW from bottom-left) with symmetric anchor cuts and a sawtooth top.
    const outline: IPoint[] = [
        [wx(0), wy(0)],
        [wx(totalLen), wy(0)],
        [wx(totalLen), wy(rightCutH)],
        [wx(totalLen - rightAnchorW), wy(bodyHeight)],
        [wx(totalLen - rightAnchorW - rightToothGap), wy(bodyHeight)],
    ];
    const teethStartX: number = leftAnchorW + leftToothGap;
    for (let i: number = N - 1; i >= 0; i--) {
        const xLeft: number = teethStartX + i * stepHyp;
        const xRight: number = teethStartX + (i + 1) * stepHyp;
        outline.push([wx(xRight), wy(bodyHeight)]);
        outline.push([wx(xLeft), wy(bodyHeight + toothHeight)]);
        outline.push([wx(xLeft), wy(bodyHeight)]);
    }
    outline.push([wx(leftAnchorW), wy(bodyHeight)]);
    outline.push([wx(0), wy(leftCutH)]);
    primitives.push({
        kind: EPrimitiveKind.Poly,
        points: outline,
        closed: true,
        fill: EColorRef.Paper2,
        stroke: EColorRef.Ink2,
        width: 1.4,
    });

    // Dimensions (zhitov m3 layout).
    primitives.push(horizontalDim(wx(0), wy(bodyHeight + toothHeight) - 28, wx(totalLen), String(Math.round(totalLen)), measurer));
    primitives.push(
        horizontalDim(wx(leftAnchorW), wy(bodyHeight + toothHeight) - 14, wx(leftAnchorW + leftToothGap), String(leftToothGap), measurer)
    );
    primitives.push(
        horizontalDim(
            wx(leftAnchorW + leftToothGap),
            wy(bodyHeight + toothHeight) - 14,
            wx(leftAnchorW + leftToothGap + stepHyp),
            String(Math.round(stepHyp)),
            measurer
        )
    );
    primitives.push(
        horizontalDim(
            wx(totalLen - rightAnchorW - rightToothGap),
            wy(bodyHeight + toothHeight) - 14,
            wx(totalLen - rightAnchorW),
            String(rightToothGap),
            measurer
        )
    );
    primitives.push(verticalDim(wx(0) - 18, wy(bodyHeight + toothHeight), wy(bodyHeight), String(toothHeight), measurer));
    primitives.push(verticalDim(wx(0) - 36, wy(bodyHeight), wy(0), String(bodyHeight), measurer));
    primitives.push(horizontalDim(wx(0), wy(0) + 18, wx(leftAnchorW), String(leftAnchorW), measurer));
    primitives.push(horizontalDim(wx(leftAnchorW), wy(0) + 18, wx(leftAnchorW + leftToothGap), String(leftToothGap), measurer));
    primitives.push(horizontalDim(wx(totalLen - rightAnchorW), wy(0) + 18, wx(totalLen), String(rightAnchorW), measurer));
    primitives.push(verticalDim(wx(totalLen) + 16, wy(rightCutH), wy(0), String(Math.round(rightCutH)), measurer));

    if (flight?.subLabel) {
        primitives.push(subLabelText([wx(0), wy(bodyHeight + toothHeight) - 40], flight.subLabel));
    }

    // Millimetre origin: the board's bottom-left corner (world x = 0, y = 0).
    return { primitives, pxPerMm: scale, origin: { x: ox, y: oy + drawH } };
}

/** The smaller of two flight fits; null when neither flight has anything to draw. */
function sharedFit(top: IProjectionOutput, bottom: IProjectionOutput): number | null {
    const fits: number[] = [top.pxPerMm, bottom.pxPerMm].filter((fit: number | null): fit is number => fit !== null);
    return fits.length === 0 ? null : Math.min(...fits);
}

/**
 * Two stacked flight drawings in one pane. They convert to millimetres together only
 * when both fits came out at the same scale; two different fits leave the pane without
 * a single millimetre scale, so it reports itself schematic.
 */
function mergeStackedFlights(top: IProjectionOutput, bottom: IProjectionOutput): IProjectionOutput {
    const primitives: IPlanPrimitive[] = [...top.primitives, ...bottom.primitives];
    if (top.pxPerMm !== bottom.pxPerMm) {
        return { primitives, pxPerMm: null, origin: { x: 0, y: 0 } };
    }
    return { primitives, pxPerMm: top.pxPerMm, origin: top.origin };
}

/**
 * Stringer drawing (чертёж тетивы / косоура) — picks тетива or косоур by
 * `config.stringerType`; for an l-shape/u-shape with two real flights it stacks
 * one drawing per flight. Pure projection in pane-local pixels.
 * 1:1 port of #drawTetiva/Kosour(ForLUShape).
 */
export function projectStringer(
    result: IStairCalculationResult,
    config: IStairConfig,
    pane: IPaneRect,
    measurer: ITextMeasurer
): IProjectionOutput {
    const core: (r: IStairCalculationResult, p: IPaneRect, m: ITextMeasurer, f?: IFlight) => IProjectionOutput =
        config.stringerType === 'kosour' ? projectKosour : projectTetiva;

    const isLU: boolean = config.type === 'l-shape' || config.type === 'u-shape';
    const flight1Count: number = result.flight1Count ?? 0;
    const flight2Count: number = result.flight2Count ?? 0;

    if (isLU && flight1Count > 0 && flight2Count > 0) {
        const h: number = result.riserHeight;
        const b: number = result.treadDepth;
        const angle: number = result.stairAngle;
        // Pane-local: split the pane in two; the drawer translates to the pane origin.
        const halfH: number = pane.h / 2;
        const topPane: IPaneRect = { x: 0, y: 0, w: pane.w, h: halfH };
        const botPane: IPaneRect = { x: 0, y: halfH, w: pane.w, h: pane.h - halfH };

        const flight1: IFlight = {
            stepCount: flight1Count,
            totalRun: flight1Count * b,
            totalRise: flight1Count * h,
            stairAngle: angle,
            subLabel: $localize`:@@planLabelMarch1:Марш 1`,
        };
        const flight2: IFlight = {
            stepCount: flight2Count,
            totalRun: flight2Count * b,
            totalRise: flight2Count * h,
            stairAngle: angle,
            subLabel: $localize`:@@planLabelMarch2:Марш 2`,
        };
        // Each flight fits its half on its own first; both are then drawn at the smaller
        // of the two fits, so the pane has one millimetre scale and the drawing file can
        // carry both boards.
        const shared: number | null = sharedFit(core(result, topPane, measurer, flight1), core(result, botPane, measurer, flight2));
        if (shared === null) {
            return emptyProjection();
        }

        return mergeStackedFlights(
            core(result, topPane, measurer, { ...flight1, maxPxPerMm: shared }),
            core(result, botPane, measurer, { ...flight2, maxPxPerMm: shared })
        );
    }

    // Pane-local: the drawer translates to the pane origin.
    return core(result, { x: 0, y: 0, w: pane.w, h: pane.h }, measurer);
}
